import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { checkCardCharge as apiCheckCardCharge, getSettings } from '../../utils/api';
import Bird from '../../assets/images/bird.svg';
import AboutKadouna from '../activation/AboutKadoona';
import Footer from '../../layout/footer/footer';
import { isMobile } from 'react-device-detect';
import './cardService.css';

function CardService() {
    const { rawCardCode } = useParams();
    const toast = useRef(null);
    const videoRef = useRef(null);

    const [setting, setSetting] = useState(() => {
        try {
            const stored = localStorage.getItem('settings');
            return stored ? JSON.parse(stored) : null;
        } catch (_) {
            return null;
        }
    });
    const [token, setToken] = useState('');
    const [isChargedFlag, setIsChargedFlag] = useState(false);
    const [chargeStatusChecked, setChargeStatusChecked] = useState(false);
    const [videoUrl, setVideoUrl] = useState('');
    const [resolvedChargedVideoUrl, setResolvedChargedVideoUrl] = useState('');
    const [chargedVideoResolving, setChargedVideoResolving] = useState(false);
    const [chargedVideoResolveError, setChargedVideoResolveError] = useState('');
    const [videoStarted, setVideoStarted] = useState(false);
    const [showAboutKadouna, setShowAboutKadouna] = useState(false);

    const routeCardDigits = useMemo(
        () => String(rawCardCode || '').replace(/\D/g, '').slice(0, 16),
        [rawCardCode]
    );
    const hasValidCardNumber = routeCardDigits.length === 16;

    const chargedVideoUrl = setting?.chargedVideoUrl
        ? String(setting.chargedVideoUrl).trim()
        : '';

    const normalizeVideoUrl = (value) =>
        String(value || '')
            .trim()
            .replace(/\/+$/, '');

    const hasCardSpecificVideo =
        Boolean(videoUrl) &&
        normalizeVideoUrl(videoUrl) !== normalizeVideoUrl(chargedVideoUrl);

    useEffect(() => {
        try {
            const userData = localStorage.getItem('userData');
            if (userData) {
                setToken(JSON.parse(userData)?.token || '');
            }
        } catch (_) {
            setToken('');
        }
    }, []);

    // settings معمولاً توسط Navbar/Home در localStorage ذخیره می‌شود؛
    // اگر صفحه مستقیم باز شده باشد، یک بار از API هم دریافتش می‌کنیم.
    useEffect(() => {
        if (setting) return;

        let active = true;

        const loadSettings = async () => {
            try {
                const response = await getSettings();
                const data = response?.data?.data || null;

                if (!active || !data) return;

                setSetting(data);
                localStorage.setItem('settings', JSON.stringify(data));
            } catch (error) {
                console.error('Unable to load settings for card service:', error);
            }
        };

        loadSettings();

        return () => {
            active = false;
        };
    }, [setting]);

    useEffect(() => {
        if (!hasValidCardNumber) {
            setChargeStatusChecked(true);
            return;
        }

        let active = true;

        const checkCard = async () => {
            setChargeStatusChecked(false);

            try {
                const res = await apiCheckCardCharge({ cardNumber: routeCardDigits });
                const data = res?.data?.data || {};

                if (!active) return;

                setIsChargedFlag(Boolean(data?.isCharged));
                setVideoUrl(data?.VideoUrl || data?.videoUrl || '');
            } catch (error) {
                if (!active) return;

                console.error('Unable to check card service status:', error);
                setIsChargedFlag(false);
                setVideoUrl('');
            } finally {
                if (active) setChargeStatusChecked(true);
            }
        };

        checkCard();

        return () => {
            active = false;
        };
    }, [hasValidCardNumber, routeCardDigits]);

    const resolveHobynaVideoUrl = async (pageUrl, signal) => {
        if (!pageUrl) return '';

        if (/\.mp4(?:$|[?#])/i.test(pageUrl)) {
            return pageUrl;
        }

        const cacheKey = `hobyna-direct-video:${pageUrl}`;

        try {
            const cached = sessionStorage.getItem(cacheKey);
            if (cached) return cached;
        } catch (_) {
            // sessionStorage may be unavailable in private/browser modes.
        }

        const parsedUrl = new URL(pageUrl, window.location.origin);

        // همان resolver استفاده‌شده در activation-dashboard؛
        // فقط صفحات ویدئویی Hobyna به MP4 مستقیم تبدیل می‌شوند.
        if (
            !/(^|\.)hobyna\.ir$/i.test(parsedUrl.hostname) ||
            !/^\/v\//i.test(parsedUrl.pathname)
        ) {
            return pageUrl;
        }

        const response = await fetch(parsedUrl.href, {
            method: 'GET',
            cache: 'force-cache',
            credentials: 'omit',
            signal
        });

        if (!response.ok) {
            throw new Error(`Hobyna resolver returned HTTP ${response.status}`);
        }

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const candidates = [
            doc.querySelector('video[src]')?.getAttribute('src'),
            doc.querySelector('video source[src]')?.getAttribute('src'),
            doc.querySelector('source[type="video/mp4"][src]')?.getAttribute('src'),
            doc.querySelector('meta[property="og:video"]')?.getAttribute('content'),
            doc.querySelector('meta[property="og:video:url"]')?.getAttribute('content'),
            doc.querySelector('meta[property="og:video:secure_url"]')?.getAttribute('content')
        ].filter(Boolean);

        const normalizedHtml = html
            .replace(/\\\//g, '/')
            .replace(/&amp;/g, '&');

        const absoluteMp4Match = normalizedHtml.match(
            /https?:\/\/[^"'<>\\\s]+\.mp4(?:\?[^"'<>\\\s]*)?/i
        );

        if (absoluteMp4Match?.[0]) {
            candidates.push(absoluteMp4Match[0]);
        }

        const relativeMp4Match = normalizedHtml.match(
            /(?:src|file|url)\s*[:=]\s*["']([^"']+\.mp4(?:\?[^"']*)?)["']/i
        );

        if (relativeMp4Match?.[1]) {
            candidates.push(relativeMp4Match[1]);
        }

        const directUrl = candidates
            .map((candidate) => {
                try {
                    return new URL(candidate, parsedUrl.href).href;
                } catch (_) {
                    return '';
                }
            })
            .find((candidate) => /\.mp4(?:$|[?#])/i.test(candidate));

        if (!directUrl) {
            throw new Error('Direct MP4 URL was not found in the Hobyna page.');
        }

        try {
            sessionStorage.setItem(cacheKey, directUrl);
        } catch (_) {
            // Cache is optional.
        }

        return directUrl;
    };

    useEffect(() => {
        setResolvedChargedVideoUrl('');
        setChargedVideoResolveError('');
        setVideoStarted(false);

        if (!chargedVideoUrl) {
            setChargedVideoResolving(false);
            return;
        }

        const controller = new AbortController();

        const resolveVideo = async () => {
            try {
                setChargedVideoResolving(true);
                const directUrl = await resolveHobynaVideoUrl(
                    chargedVideoUrl,
                    controller.signal
                );

                if (!controller.signal.aborted) {
                    setResolvedChargedVideoUrl(directUrl);
                }
            } catch (error) {
                if (error?.name === 'AbortError') return;

                console.error('Unable to resolve chargedVideoUrl:', error);

                if (!controller.signal.aborted) {
                    setChargedVideoResolveError('ویدئوی معرفی در دسترس نیست.');
                }
            } finally {
                if (!controller.signal.aborted) {
                    setChargedVideoResolving(false);
                }
            }
        };

        resolveVideo();

        return () => controller.abort();
    }, [chargedVideoUrl]);

    const playIntroVideo = async () => {
        if (!resolvedChargedVideoUrl || !videoRef.current) return;

        setVideoStarted(true);

        try {
            await videoRef.current.play();
        } catch (error) {
            console.error('Unable to autoplay card service video:', error);
        }
    };

    const dashboardItems = [
        {
            label: 'ویدئو روی کارت',
            description: 'مشاهده ویدئوهای اختصاصی',
            icon: '▶',
            tone: 'red',
            url: videoUrl,
            external: /^https?:\/\//i.test(videoUrl || ''),
            disabled: !chargeStatusChecked || !hasCardSpecificVideo
        },
        {
            label: 'شارژ کارت',
            description: 'افزایش موجودی کارت',
            icon: '▦',
            tone: 'blue',
            url: `/activation/${routeCardDigits}?view=charge`,
            disabled: !chargeStatusChecked || isChargedFlag
        },
        {
            label: 'استعلام کارت',
            description: 'مشاهده موجودی و تراکنش‌ها',
            icon: '$',
            tone: 'green',
            url: `/balance/${routeCardDigits}`,
            disabled: !hasValidCardNumber
        },
        {
            label: 'سفارش کارت جادویی',
            description: 'درخواست صدور کارت',
            icon: '✦',
            tone: 'purple',
            url: '/design'
        },
        {
            label: 'ویرایش کارت',
            description: 'ویرایش اطلاعات کارت',
            icon: '✎',
            tone: 'orange',
            url: '/panel?section=cards',
            disabled: !token
        },
        {
            label: 'سرگرمی',
            description: 'بازی‌ها و محتوای ویژه',
            icon: '✚',
            tone: 'green2',
            url: 'https://hobyna.ir/',
            external: true
        },
        {
            label: 'همکاری با ما',
            description: 'نمایندگی و همکاری سازمانی',
            icon: '⇄',
            tone: 'peach',
            url: '/contact-us'
        },
        {
            label: 'کاتالوگ',
            description: 'مشاهده محصولات و خدمات',
            icon: '▤',
            tone: 'violet',
            url: '/catalog'
        },
        {
            label: 'پشتیبانی',
            description: 'مشاوره و ارتباط با ما',
            icon: '◉',
            tone: 'sky',
            url: '/contact-us'
        },
        {
            label: 'معرفی',
            description: 'آشنایی با کادونا',
            icon: 'i',
            tone: 'cyan',
            action: 'about'
        }
    ];

    if (showAboutKadouna) {
        return (
            <div className="card-service-about-shell" dir="rtl">
                <AboutKadouna onBack={() => setShowAboutKadouna(false)} />
            </div>
        );
    }

    return (
        <div className="card-service-page" dir="rtl">
            <Toast ref={toast} />

            <div className="card-service-decor card-service-decor-dots" aria-hidden="true" />
            <div className="card-service-decor card-service-decor-circle" aria-hidden="true" />

            <main className="card-service-container">
                <header className="card-service-heading">
                    <div className="card-service-heading-copy">
                        <div className="card-service-title-row">
                            <div>
                                <h1> خدمات هوشمند کارت</h1>
                            </div>
                            <img className="card-service-bird" src={Bird} alt="" aria-hidden="true" />
                        </div>
                    </div>
                </header>

                {!hasValidCardNumber && (
                    <div className="card-service-invalid-card">
                        شماره کارت موجود در آدرس معتبر نیست. لطفاً از صفحه اصلی دوباره وارد شوید.
                    </div>
                )}

                <section className="card-service-main-layout">
                    <section className="card-service-services" aria-label="خدمات کارت">
                        {dashboardItems.map((item, index) => {
                            const className = `card-service-item item-${index + 1} ${item.disabled ? 'is-disabled' : ''}`;

                            if (item.action === 'about') {
                                return (
                                    <button
                                        key={item.label}
                                        type="button"
                                        className={className}
                                        onClick={() => setShowAboutKadouna(true)}
                                    >
                                        <span className={`card-service-item-icon ${item.tone}`}>{item.icon}</span>
                                        <span className="card-service-item-copy">
                                            <strong>{item.label}</strong>
                                            <small>{item.description}</small>
                                        </span>
                                    </button>
                                );
                            }

                            return (
                                <a
                                    key={item.label}
                                    className={className}
                                    href={item.disabled ? undefined : item.url}
                                    aria-disabled={item.disabled ? 'true' : undefined}
                                    tabIndex={item.disabled ? -1 : 0}
                                    target={!item.disabled && item.external ? '_blank' : undefined}
                                    rel={!item.disabled && item.external ? 'noreferrer' : undefined}
                                    title={
                                        item.label === 'شارژ کارت' && isChargedFlag
                                            ? 'این کارت قبلاً شارژ شده است'
                                            : undefined
                                    }
                                >
                                    <span className={`card-service-item-icon ${item.tone}`}>{item.icon}</span>
                                    <span className="card-service-item-copy">
                                        <strong>{item.label}</strong>
                                        <small>{item.description}</small>
                                    </span>
                                </a>
                            );
                        })}
                    </section>
<section className="card-service-video-column" aria-label="ویدئوی معرفی">
    <div className="card-service-video-box">
        {resolvedChargedVideoUrl ? (
            <video
                ref={videoRef}
                className="card-service-video"
                src={resolvedChargedVideoUrl}
                autoPlay
                muted
                controls
                playsInline
                preload="auto"
                onPlay={() => setVideoStarted(true)}
            />
        ) : null}

        {!videoStarted && !resolvedChargedVideoUrl && (
            <div
                className="card-service-video-overlay"
                aria-label="ویدیو"
            >
                {chargedVideoResolving ? (
                    <>
                        <span className="card-service-video-spinner" />
                        <span className="card-service-video-text">
                            در حال آماده‌سازی ویدئو...
                        </span>
                    </>
                ) : (
                    <>
                        <span className="card-service-video-play" aria-hidden="true">
                            ▶
                        </span>

                        <span className="card-service-video-text">
                            {chargedVideoResolveError || 'ویدیویی برای نمایش موجود نیست'}
                        </span>
                    </>
                )}
            </div>
        )}
    </div>
</section>
                </section>

            </main>

       {!isMobile && <Footer />}
        </div>
    );
}

export default CardService;
